import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Button, Stack, Typography } from "@mui/material";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import SportsEsportsOutlinedIcon from "@mui/icons-material/SportsEsportsOutlined";
import { useGetProgressQuery } from "@store/gameApi";
import { RootState } from "@store/store";
import type { GameProgressResponse } from "@models/game";
import DashboardTile from "@components/Home/DashboardTile/DashboardTile";
import DomainRadarMiniChart from "@components/Home/Games/GamesCTATile/DomainRadarMiniChart";
import LastSessionSummaryLine from "@components/Home/Games/GamesCTATile/LastSessionSummaryLine";
import WeeklyActivityBar from "@components/Home/Games/GamesCTATile/WeeklyActivityBar";

const PatientEncouragement: FC<{ progress: GameProgressResponse | null | undefined }> = ({ progress }) => {
  const { t } = useTranslation();

  const message = useMemo(() => {
    const trend = progress?.trend ?? [];
    const streak = progress?.streakDay ?? 0;
    if (trend.length === 0) return t("games.cta.encouragement.firstTime");
    const dates = trend.map((e) => e.date?.slice(0, 10)).filter(Boolean);
    const lastDateStr = dates.length ? [...dates].sort().pop()! : "";
    const last = new Date(lastDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    last.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((today.getTime() - last.getTime()) / 86400000);
    if (diffDays === 0) return t("games.cta.encouragement.playedToday");
    if (diffDays === 1) return t("games.cta.encouragement.playedYesterday");
    return t("games.cta.encouragement.keepGoing");
  }, [progress, t]);

  const streak = progress?.streakDay ?? 0;

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 3,
        bgcolor: "primary.50",
        background: "linear-gradient(135deg, #EEF4FF 0%, #F0F7FF 100%)",
        textAlign: "center",
      }}
    >
      {streak > 1 ? (
        <Stack direction="row" justifyContent="center" alignItems="center" gap={1} mb={0.5}>
          <LocalFireDepartmentIcon sx={{ color: "warning.main", fontSize: 28 }} />
          <Typography variant="h5" fontWeight={700} color="warning.dark">
            {streak}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t("games.cta.streakDays")}
          </Typography>
        </Stack>
      ) : (
        <EmojiEventsOutlinedIcon sx={{ color: "primary.main", fontSize: 36, mb: 0.5 }} />
      )}
      <Typography variant="body1" fontWeight={500} color="text.primary">
        {message}
      </Typography>
    </Box>
  );
};

export interface GamesCTATileProps {
  /** Base path for navigation (e.g. "" for caregiver, "portal" for patient) */
  basePath?: string;
}

const GamesCTATile: FC<GamesCTATileProps> = ({ basePath = "" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const { data: progress } = useGetProgressQuery(
    { patientId: selectedPatientId! },
    { skip: !selectedPatientId },
  );
  const prefix = basePath ? `/${basePath}` : "";
  const isPortal = basePath === "portal";

  const handleStartSession = () => {
    navigate(`${prefix}/games/guided`);
  };

  const handlePlayAGame = () => {
    navigate(`${prefix}/games`);
  };

  const handleRadarTap = () => {
    navigate(`${prefix}/games`);
  };

  return (
    <DashboardTile title={t("games.cta.title")}>
      {progress?.streakDay != null && progress.streakDay > 0 && (
        <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 1.5 }}>
          <LocalFireDepartmentIcon sx={{ color: "warning.main", fontSize: 20 }} />
          <Typography variant="body2">
            {t("games.cta.streak", { count: progress.streakDay })}
          </Typography>
        </Stack>
      )}
      <Stack spacing={2}>
        <Stack spacing={0.25}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleStartSession}
          >
            <Stack spacing={0.25}>
              {t("games.cta.startSession")}
              <Typography variant="body2" sx={{ px: 0.5, opacity: 0.95 }}>
                {t("games.cta.startSessionSubtitle")}
              </Typography>
            </Stack>
          </Button>
        </Stack>
        <Stack spacing={0.25}>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            startIcon={<SportsEsportsOutlinedIcon />}
            onClick={handlePlayAGame}
          >
            {t("games.cta.playAGame")}
          </Button>
        </Stack>
      </Stack>

      <Stack spacing={2} sx={{ mt: 3 }}>
        {isPortal ? (
          <PatientEncouragement progress={progress} />
        ) : (
          <DomainRadarMiniChart progress={progress ?? null} onTap={handleRadarTap} />
        )}
        {!isPortal && <LastSessionSummaryLine progress={progress ?? null} />}
        <WeeklyActivityBar progress={progress ?? null} />
      </Stack>
    </DashboardTile>
  );
};

export default GamesCTATile;
